import {
  FETCH_ALL_USERS,
  FETCH_ALL_USERS_SUCCESS,
  FETCH_ALL_USERS_FAILED,
  EDIT_USER,
  EDIT_USER_SUCCESS,
  EDIT_USER_FAILED,
  DELETE_USER,
  DELETE_USER_SUCCESS,
  DELETE_USER_FAILED,
  FETCH_ALL_USERS_STATIC,
  FETCH_ALL_USERS_STATIC_SUCCESS,
  FETCH_ALL_USERS_STATIC_FAILED,
  FETCH_CUSTOMERS_PAGE,
  FETCH_CUSTOMERS_PAGE_SUCCESS,
  FETCH_CUSTOMERS_PAGE_FAILED,
  USER_DELETED,
  FETCH_ALL_DRIVERS,
  FETCH_ALL_DRIVERS_SUCCESS,
  FETCH_ALL_DRIVERS_FAILED,
  FETCH_OTP_AUTH_REQUESTS,
  FETCH_OTP_AUTH_REQUESTS_SUCCESS,
  FETCH_OTP_AUTH_REQUESTS_FAILED,
  UPSERT_USER,
  REMOVE_USER,
  UPSERT_DRIVER_CARS
} from "../store/types";
import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { set, push, update, off, get, onValue, remove, query, orderByChild, equalTo } from "firebase/database";
import { uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { signOut } from "firebase/auth";
import AccessKey from '../other/AccessKey';
import base64 from 'react-native-base64';
import { createIDBCache } from '../other/idbCache';

import { postWithIdToken } from './mileactions';
// IndexedDB cache for fetchUsers / fetchUsersOnce. Switched from
// sessionStorage because the production /users tree at this customer's
// scale (~36k records, ~30 MB) blows past sessionStorage's ~5–10 MB
// quota. IndexedDB has GB-level budgets so the full payload fits.
//
// TTL is the staleness budget for changes made by OTHER actors — mobile
// signups, other admins, system writes. The current admin's own edits
// patch the cache directly via patchUsersCacheUpsert/Delete, so they're
// never delayed by TTL regardless of length.
//
// 4-hour TTL is calibrated to this customer's signup rate: ~40-60 new
// drivers/day + ~300 new customers/day = ~15 new users/hour. Worst case
// an admin sees a cache that's missing the last ~60 mobile signups —
// acceptable for nearly every admin workflow (the search/lookup screens
// don't depend on minute-fresh data). Bump higher (12h, 24h) if cost
// becomes critical and admins rarely need to act on brand-new signups.
const USERS_CACHE_TTL_MS = 4 * 60 * 60 * 1000;
const usersCache = createIDBCache('donkey_users_cache', 'users', USERS_CACHE_TTL_MS);
const USERS_KEY_MAIN = 'main';     // fetchUsers (excludes admins, has location)
const USERS_KEY_STATIC = 'static'; // fetchUsersOnce (includes everyone, no location)

// Call-center roles load only NON-customers (drivers + staff) into the
// users/staticusers slices and resolve customers on-demand / paginated, so a
// cold call-center session never downloads the ~45k-customer /users tree.
// admin/finance/fleetadmin keep the existing full /users load unchanged.
const CALL_CENTER_ROLES = ['callcenteragent', 'callcentersupervisor', 'scout'];
const currentRole = () => {
  try { return store.getState().auth.profile.usertype; } catch (_) { return null; }
};
// Master rollback flag. When settings.useCustomerOnDemand is OFF, EVERY role
// keeps the existing full /users load + full caches (byte-identical to before).
// When ON, call-center roles load only drivers + staff and customers are
// paginated / resolved on-demand. Gating the slim path here means the cache
// keys, the merged load, and the patch guards all flip together off one flag.
const useCustomerOnDemand = () => {
  try { return store.getState().settingsdata.settings.useCustomerOnDemand === true; } catch (_) { return false; }
};
const isCallCenterRole = () =>
  CALL_CENTER_ROLES.indexOf(currentRole()) >= 0 && useCustomerOnDemand();
// Non-customer usertypes the merged call-center load pulls. The main `users`
// slice has always excluded admins; the static slice includes them.
const NON_CUSTOMER_TYPES_MAIN = ['driver', 'fleetadmin', 'finance', 'callcenteragent', 'callcentersupervisor', 'marketing', 'scout'];
const NON_CUSTOMER_TYPES_STATIC = ['admin'].concat(NON_CUSTOMER_TYPES_MAIN);

// Role-scoped cache keys so a call-center (drivers+staff) cache never collides
// with an admin/finance (full) cache on a shared workstation. Computed live on
// every call from the current role.
const roleSuffix = () => (isCallCenterRole() ? ':cc' : '');
const keyMain = () => USERS_KEY_MAIN + roleSuffix();
const keyStatic = () => USERS_KEY_STATIC + roleSuffix();

const readUsersCache = () => usersCache.read(keyMain(), 'usersCache');
const writeUsersCache = (users, key) => usersCache.write(key || keyMain(), users, 'usersCache');
const readStaticUsersCache = () => usersCache.read(keyStatic(), 'staticUsersCache');
const writeStaticUsersCache = (users, key) => usersCache.write(key || keyStatic(), users, 'staticUsersCache');

// Separate IDB DB for the paginated customer list (Riders) — kept off the
// donkey_users_cache lifecycle so main/static are never disturbed. Accumulates
// the default newest-first feed across "load more" so revisits are zero-network.
const customersCache = createIDBCache('donkey_customers_cache', 'customers', USERS_CACHE_TTL_MS);
const CUSTOMERS_KEY_DEFAULT = 'default';
const readCustomersPaged = () => customersCache.read(CUSTOMERS_KEY_DEFAULT, 'customersPaged');
const writeCustomersPaged = (val) => customersCache.write(CUSTOMERS_KEY_DEFAULT, val, 'customersPaged');

// In-flight guards — collapse concurrent COLD fetches into ONE network read.
// ~several components dispatch fetchUsers/fetchUsersOnce on mount; the cache
// check is async, so without this each misses the cache and fires its own full
// ~22 MB /users read (the profiled "10× /users reads in one session" stampede).
// Keyed by cache-key + '|' + signed-in uid so a full and a call-center (:cc)
// read never clobber each other, and a second user on the same tab never joins
// the previous user's load, while same-shape concurrent reads still collapse
// to one. (The uid is in these MAP keys only — never in the IndexedDB keys.)
const usersMainInflight = {};
const usersStaticInflight = {};

// Shared RAW user-list read behind BOTH loaders. The two guards above are per
// slice, so every portal login still downloaded the list TWICE within seconds:
// fetchUsers ('main') and fetchUsersOnce ('static') each missed their own IDB
// cache and each ran their own read (profiled: 2 x 42 MB /users for
// admin/finance, 2 x 18 MB drivers query for call-center).
// getRawUsersShared(scope) collapses them into one download:
//   - scope 'full' = one get(usersRef); scope 'cc' = the call-center fan-out
//     over usersByTypeRef for the UNION of types both loaders need (static's
//     list, incl. 'admin'; main drops admins as it always has). Scopes never
//     cross: a 'cc' read can't satisfy a 'full' request or vice-versa.
//   - one in-flight promise per scope AND signed-in uid: concurrent main +
//     static cold loads share a single network read, and a second user on the
//     same tab can never join the previous user's read;
//   - a short-lived memo of the raw value: a static load that starts a few
//     seconds after main finished does not download again.
// The raw value is READ-ONLY: each loader derives its own row objects
// ({ ...row, id }) so main's location join can never leak into the static rows
// (the Edit User form set()s a static row straight back to /users).
// Every user mutation / invalidate calls dropRawUsersShared(), so the memo can
// never hide an edit on a session whose IDB cache is unavailable; rawUsersGen
// also stops a read that was already in flight from memoizing pre-edit data.
// The memo is bound to the uid that STARTED the read: the uid is captured
// before the read, the value is memoized only if that same uid is still signed
// in when it lands, and a null uid never hits the memo. The web-only
// store.subscribe below also drops everything the moment auth.profile.uid
// changes (incl. to null on sign-out). The memo is released by a timer at TTL
// and is web-only: React Native never memoizes and never subscribes. Mobile
// does not call fetchUsers / fetchUsersOnce; the gate keeps that true by
// construction (same check as other/settingsOnce.js).
const RAW_USERS_MEMO_TTL_MS = 120 * 1000;
const rawUsersInflight = {}; // scope + '|' + uid -> Promise<{ uid: row } | null>
const rawUsersMemo = {};     // scope -> { value, at, uid }
let rawUsersGen = 0;
const isReactNative = () =>
  typeof window === 'undefined' ||
  typeof document === 'undefined' ||
  (typeof navigator !== 'undefined' && navigator.product === 'ReactNative');
const currentUid = () => {
  try { return store.getState().auth.profile.uid || null; } catch (_) { return null; }
};
const dropRawUsersShared = () => {
  rawUsersGen++;
  Object.keys(rawUsersMemo).forEach((k) => { delete rawUsersMemo[k]; });
  Object.keys(rawUsersInflight).forEach((k) => { delete rawUsersInflight[k]; });
};
const readRawUsers = async (scope) => {
  const { usersRef, usersByTypeRef } = firebase;
  if (scope === 'cc') {
    const results = await Promise.all(NON_CUSTOMER_TYPES_STATIC.map(t => get(usersByTypeRef(t))));
    const merged = {};
    results.forEach(s => { const v = s && s.val(); if (v) Object.keys(v).forEach(k => { merged[k] = v[k]; }); });
    return Object.keys(merged).length ? merged : null;
  }
  const usersSnap = await get(usersRef);
  return usersSnap.val() || null;
};
// `force` (fetchUsersOnce(true)) skips the memo AND any read already in flight,
// always hits the network, and its result then refreshes the memo.
const getRawUsersShared = (scope, force) => {
  // Whoever is signed in when the read STARTS owns it: captured once here,
  // used for the in-flight key, and checked again when the read lands.
  const uid = currentUid();
  const inflightKey = scope + '|' + uid;
  if (force) {
    rawUsersGen++; // an older in-flight read must not overwrite the fresher memo
  } else {
    const memo = rawUsersMemo[scope];
    if (uid && memo && memo.uid === uid && Date.now() - memo.at < RAW_USERS_MEMO_TTL_MS) {
      return Promise.resolve(memo.value);
    }
    if (rawUsersInflight[inflightKey]) return rawUsersInflight[inflightKey];
  }
  const gen = rawUsersGen;
  // Cleared inside the handlers (before the value reaches any caller) so a
  // settled promise is never handed out as "in flight".
  const settle = () => { if (rawUsersInflight[inflightKey] === p) delete rawUsersInflight[inflightKey]; };
  const p = readRawUsers(scope).then(
    (value) => {
      settle();
      // Never memoize for nobody, nor for a user other than the one who
      // started the read (sign-out / another login while it was running).
      if (value && uid && currentUid() === uid && gen === rawUsersGen && !isReactNative()) {
        const entry = { value: value, at: Date.now(), uid: uid };
        rawUsersMemo[scope] = entry;
        setTimeout(() => { if (rawUsersMemo[scope] === entry) delete rawUsersMemo[scope]; }, RAW_USERS_MEMO_TTL_MS);
      }
      return value;
    },
    (error) => { settle(); throw error; }
  );
  rawUsersInflight[inflightKey] = p;
  return p;
};
// Web-only sign-off hook (authactions.js is shared with mobile and untouched):
// the moment auth.profile.uid changes — login, sign-out (-> null) or another
// user on the same tab — drop the memo and every in-flight entry so nothing
// read for one user can be handed to the next. Compares a primitive and only
// touches this module's maps (no dispatch, no network read). Never installed
// on React Native.
if (!isReactNative()) {
  try {
    let lastRawUsersUid = currentUid();
    store.subscribe(() => {
      const uid = currentUid();
      if (uid !== lastRawUsersUid) {
        lastRawUsersUid = uid;
        dropRawUsersShared();
      }
    });
  } catch (_) { /* no store — the uid captured in getRawUsersShared still guards */ }
}

// Drop both user caches so the next fetchUsers/fetchUsersOnce hits the
// network. Last-resort escape hatch — prefer patchUsersCache* below for
// known mutations so admins don't trigger a full re-fetch after every edit.
const invalidateUsersCache = () => {
  dropRawUsersShared();
  usersCache.invalidate(USERS_KEY_MAIN);
  usersCache.invalidate(USERS_KEY_STATIC);
  usersCache.invalidate(USERS_KEY_MAIN + ':cc');
  usersCache.invalidate(USERS_KEY_STATIC + ':cc');
  customersCache.invalidate(CUSTOMERS_KEY_DEFAULT);
};

// Surgical cache patch for a user upsert. Merges `userPatch` into the
// existing cache entry (or inserts if absent) — works for both full
// replacements (editUser uses `set()`) and partial updates (updateUserCar /
// updateLicenseImage use `update()`). fetchUsers excludes admins and
// joins location; fetchUsersOnce includes everyone and has no location —
// patches respect both shapes.
//
// Also dispatches UPSERT_USER to Redux so currently-rendered admin views
// (DriverDetails wallet/topup tab, UserRides, Withdraws, etc.) re-render
// with fresh data immediately, without waiting for next page reload.
//
// Exported so other actions (notably bookinglistactions.updateBooking
// and refreshUsersAfterSettlement) can keep the user state coherent
// when they mutate driver fields as a side effect of booking changes.
export const patchUsersCacheUpsert = async (id, userPatch) => {
  if (!id || !userPatch) return;
  dropRawUsersShared();
  // Dispatch first — Redux is the live source for mounted components.
  // The cache write below only matters for the next page reload.
  try {
    store.dispatch({ type: UPSERT_USER, payload: { id, patch: userPatch } });
  } catch (_) { /* ignore — dispatch shouldn't fail */ }
  const cc = isCallCenterRole();
  try {
    await usersCache.patch(keyMain(), (current) => {
      if (!Array.isArray(current)) return null;
      const prev = current.find(u => u.id === id);
      const next = current.filter(u => u.id !== id);
      const mergedUser = { ...(prev || {}), ...userPatch, id };
      // `main` always excludes admins; the call-center (:cc) shape also
      // excludes customers (they live in the paginated customers cache).
      if (mergedUser.usertype !== 'admin' && !(cc && mergedUser.usertype === 'customer')) {
        mergedUser.location = mergedUser.location || (prev && prev.location) || null;
        next.push(mergedUser);
      }
      return next;
    });
    await usersCache.patch(keyStatic(), (current) => {
      if (!Array.isArray(current)) return null;
      const prev = current.find(u => u.id === id);
      const next = current.filter(u => u.id !== id);
      const mergedUser = { ...(prev || {}), ...userPatch, id };
      // Full static includes everyone; the :cc shape excludes customers.
      if (!(cc && mergedUser.usertype === 'customer')) {
        next.push(mergedUser);
      }
      return next;
    });
    // Keep the paginated customers cache coherent for an edited customer —
    // only the call-center on-demand path maintains that cache, so skip the
    // extra IDB transaction entirely for full-load roles / flag-off.
    if (cc) await patchCustomersPagedCache(id, userPatch);
  } catch (_) { invalidateUsersCache(); }
};

// Bulk form of patchUsersCacheUpsert for server-side mass edits (Security →
// "Fill Vehicle Placeholders" stamps thousands of drivers at once). One Redux
// dispatch per id (React batches the renders) and ONE rewrite of each IDB
// cache instead of one per id — the per-id helper rewrites the whole cached
// users array twice per call. Only merges into rows that are already cached
// (never inserts); customers are not touched (the paginated feed is left
// alone), so use the single-id helper for customer edits.
export const patchUsersCacheBulk = async (patchesById) => {
  const ids = patchesById ? Object.keys(patchesById) : [];
  if (!ids.length) return;
  dropRawUsersShared();
  try {
    ids.forEach((id) => {
      store.dispatch({ type: UPSERT_USER, payload: { id, patch: patchesById[id] } });
    });
  } catch (_) { /* ignore — dispatch shouldn't fail */ }
  const merge = (current) => {
    if (!Array.isArray(current)) return null;
    return current.map((u) => (u && patchesById[u.id] ? { ...u, ...patchesById[u.id] } : u));
  };
  try {
    await usersCache.patch(keyMain(), merge);
    await usersCache.patch(keyStatic(), merge);
  } catch (_) { invalidateUsersCache(); }
};

// Merge an edited customer into the cached paginated feed (if it's loaded), or
// drop it if it stopped being a customer. Never inserts a new customer — the
// cache only holds what was actually fetched.
const patchCustomersPagedCache = async (id, userPatch) => {
  try {
    await customersCache.patch(CUSTOMERS_KEY_DEFAULT, (current) => {
      if (!current || !current.byId || !current.byId[id]) return null; // not loaded → no change
      const merged = { ...current.byId[id], ...userPatch, id };
      if (merged.usertype && merged.usertype !== 'customer') {
        const byId = { ...current.byId }; delete byId[id];
        return { ...current, byId, rows: (current.rows || []).filter(r => r.id !== id) };
      }
      const byId = { ...current.byId, [id]: merged };
      return { ...current, byId, rows: (current.rows || []).map(r => r.id === id ? merged : r) };
    });
  } catch (_) { /* best-effort cache coherence */ }
};

// Surgical cache patch for delete. Splices the user out of both caches
// AND dispatches REMOVE_USER so any list view re-renders without the
// removed user (no need to wait for refetch). Exported so other actions
// (and the customer pagination path) can keep state coherent on deletes.
export const patchUsersCacheDelete = async (id) => {
  if (!id) return;
  dropRawUsersShared();
  try {
    store.dispatch({ type: REMOVE_USER, payload: id });
  } catch (_) { /* ignore */ }
  try {
    const splice = (current) => Array.isArray(current) ? current.filter(u => u.id !== id) : null;
    await usersCache.patch(keyMain(), splice);
    await usersCache.patch(keyStatic(), splice);
    if (isCallCenterRole()) await customersCache.patch(CUSTOMERS_KEY_DEFAULT, (current) => {
      if (!current || !current.byId || !current.byId[id]) return null;
      const byId = { ...current.byId }; delete byId[id];
      return { ...current, byId, rows: (current.rows || []).filter(r => r.id !== id) };
    });
  } catch (_) { invalidateUsersCache(); }
};

// Builds the `users` slice payload. admin/finance/fleetadmin get the full
// /users tree (admins filtered out) joined with /locations — unchanged. A
// call-center role instead loads only the NON-customer usertypes via the
// indexed usersByTypeRef query (drivers + staff), so the ~45k customers are
// never downloaded. Output shape is identical either way.
//
// The user rows come from getRawUsersShared (one download shared with
// loadStaticUsers). The /locations join is bounded to rows updated in the last
// 24 h (recentLocationsRef, indexed on updateTime) instead of the full node:
// no portal view reads `.location` off these rows (the Dashboard map has its
// own live feed), every row still carries the `location` key, and a driver
// whose last fix is older than 24 h gets null — same as a driver with no row.
const LOCATION_JOIN_WINDOW_MS = 24 * 3600 * 1000;
const loadMainUsers = async () => {
  const { recentLocationsRef } = firebase;
  const locationsQuery = recentLocationsRef(Date.now() - LOCATION_JOIN_WINDOW_MS);
  if (isCallCenterRole()) {
    const [locationSnap, merged] = await Promise.all([get(locationsQuery), getRawUsersShared('cc')]);
    const locations = (locationSnap && locationSnap.val()) || {};
    if (!merged) return null;
    // The shared 'cc' read is the static union (incl. admins); `main` has
    // always excluded them.
    const rows = Object.keys(merged)
      .filter(i => merged[i].usertype !== 'admin')
      .map(i => ({ ...merged[i], id: i, location: locations[i] || null }));
    return rows.length ? rows : null;
  }
  const [data, locationSnap] = await Promise.all([getRawUsersShared('full'), get(locationsQuery)]);
  if (!data) return null;
  const locations = locationSnap.val();
  return Object.keys(data)
    .filter(i => data[i].usertype != 'admin')
    .map(i => ({ ...data[i], id: i, location: locations && locations[i] ? locations[i] : null }));
};

// Builds the `staticusers` slice payload (no location join). Full set for
// admin/finance/fleetadmin (includes admins); drivers + staff + admin for
// call-center (customers excluded — resolved on-demand / paginated).
// Rows come from getRawUsersShared (one download shared with loadMainUsers);
// `force` (fetchUsersOnce(true)) bypasses its memo and always hits the network.
const loadStaticUsers = async (force) => {
  const data = await getRawUsersShared(isCallCenterRole() ? 'cc' : 'full', force);
  if (!data) return null;
  return Object.keys(data).map(i => ({ ...data[i], id: i }));
};

export const fetchUsers = () => async (dispatch) => {

  // Cache hit — hydrate Redux instantly, no WebSocket traffic. The
  // IndexedDB check is what the user sees as "no /users download on
  // reload" in the network tab. Role-scoped key so a call-center session
  // never reads an admin's full-tree cache (or vice-versa).
  const cached = await readUsersCache();
  if (cached) {
    dispatch({
      type: FETCH_ALL_USERS_SUCCESS,
      payload: cached,
    });
    return;
  }

  dispatch({
    type: FETCH_ALL_DRIVERS,
    payload: null
  });
  try {
    // Both captured BEFORE the await: the result is written under the cache
    // key of the role that STARTED the load (recomputing it afterwards could
    // put user A's full tree under user B's ':cc' key after a sign-out +
    // sign-in), and only the same signed-in user may join this load.
    const key = keyMain();
    const inflightKey = key + '|' + currentUid();
    // Stampede guard keyed by cache-shape: collapse concurrent cold reads of
    // the same shape into one network read.
    if (!usersMainInflight[inflightKey]) {
      const p = (async () => {
        const arr = await loadMainUsers();
        if (arr) writeUsersCache(arr, key);
        return arr;
      })();
      usersMainInflight[inflightKey] = p;
      p.catch(() => {}).then(() => { if (usersMainInflight[inflightKey] === p) delete usersMainInflight[inflightKey]; });
    }
    const arr = await usersMainInflight[inflightKey];
    if (arr) {
      dispatch({
        type: FETCH_ALL_USERS_SUCCESS,
        payload: arr
      });
    } else {
      dispatch({
        type: FETCH_ALL_USERS_FAILED,
        payload: "No users available."
      });
    }
  } catch (error) {
    dispatch({
      type: FETCH_ALL_USERS_FAILED,
      payload: error.message || "Error fetching users."
    });
  }
};


// Static-snapshot users fetch used by ResponsiveDrawer (every admin page
// load), Complain, InternalStaff, DriverLetterGenerator, EditCarType, etc.
// None of those consumers reference `.location`, so the previous parallel
// `/locations` download was pure waste — locations is large (one row per
// active driver, mutated constantly) and was being re-downloaded on every
// page mount. fetchUsers (the dynamic listener that feeds Dashboard /
// AddBookings) still pulls locations because those pages do need them.
// Same IndexedDB caching as fetchUsers but separate cache key — this
// version omits the .location join and includes admins, so a different
// shape.
export const fetchUsersOnce = (force) => async (dispatch) => {

  // Cache hit — skip the network entirely. Role-scoped key. `force`
  // bypasses the cache READ (fresh refetch; the cache is rewritten
  // below) — used by admin bulk actions that must not act on stale
  // rows (e.g. appVersion values from hours ago). Returns the list so
  // callers can filter it directly.
  if (!force) {
    const cachedStatic = await readStaticUsersCache();
    if (cachedStatic) {
      dispatch({
        type: FETCH_ALL_USERS_STATIC_SUCCESS,
        payload: cachedStatic,
      });
      return cachedStatic;
    }
  }

  dispatch({
    type: FETCH_ALL_USERS_STATIC,
    payload: null
  });
  try {
    // Both captured BEFORE the await — see fetchUsers.
    const key = keyStatic();
    const inflightKey = key + '|' + currentUid();
    // Stampede guard keyed by cache-shape: collapse concurrent cold fetches of
    // the same shape into one /users read.
    if (!usersStaticInflight[inflightKey]) {
      const p = (async () => {
        const arr = await loadStaticUsers(force);
        if (arr) writeStaticUsersCache(arr, key);
        return arr;
      })();
      usersStaticInflight[inflightKey] = p;
      p.catch(() => {}).then(() => { if (usersStaticInflight[inflightKey] === p) delete usersStaticInflight[inflightKey]; });
    }
    const arr = await usersStaticInflight[inflightKey];
    if (arr) {
      dispatch({
        type: FETCH_ALL_USERS_STATIC_SUCCESS,
        payload: arr
      })
    } else {
      dispatch({
        type: FETCH_ALL_USERS_STATIC_FAILED,
        payload: "No users available."
      });
    }
    return arr || null;
  } catch (error) {
    dispatch({
      type: FETCH_ALL_USERS_STATIC_FAILED,
      payload: error.message || "Error fetching users."
    });
    return null;
  }
};
// One-shot, indexed drivers-only read (driversRef, usertype === 'driver') with
// NO Redux dispatch and no cache — for admin bulk actions that need fresh
// driver rows (SecurityDashboard's flat-rate enroll) without forcing the full
// ~30 MB /users refetch that fetchUsersOnce(true) does. Resolves to the same
// { ...user, id } row shape as the staticusers slice; [] when empty so callers
// can fall back to the cached slice.
export const fetchDriversOnce = () => async () => {
  const { driversRef } = firebase;
  const snap = await get(driversRef);
  const data = snap.val();
  if (!data) return [];
  return Object.keys(data).map(i => { data[i].id = i; return data[i]; });
};

// Normalize a phone search input to the stored +251 E.164 form so the indexed
// `mobile` equalTo lookup matches.
const normalizeMobileSearch = (s) => {
  let d = (s || '').replace(/[^0-9]/g, '');
  if (d.indexOf('251') === 0) d = d.slice(3);
  if (d.indexOf('0') === 0) d = d.slice(1);
  return '+251' + d;
};

// Cap on filtered/search reads so a broad cohort (e.g. "without invited
// contacts") can never pull the whole customer base. Exact totals come from
// the stats node, not by counting rows.
const CUSTOMERS_FILTER_CAP = 1000;

// One page of the customer list, read from the server-maintained
// /customer_index (customers ONLY) instead of the full /users tree. The
// default feed is newest-first and cursor-paginated; filters and search each
// run a single indexed query. Pages accumulate into the usersdata.customers
// slice + a dedicated IndexedDB cache so "load more" and revisits stay cheap.
//   opts.cursor   — oldest createdAt already loaded (for "load more")
//   opts.pageSize — default 25
//   opts.filter   — 'invited' | 'without_invited' | 'appless' | null
//   opts.search   — name or phone string | null
//   opts.reset    — force a fresh network read (bypass cache), replace rows
export const fetchCustomersPage = (opts = {}) => async (dispatch) => {
  const { cursor = null, pageSize = 25, filter = null, search = null, reset = false } = opts;
  const {
    customersPageRef, customersByInvitedRef, customersAppLessRef,
    customersByMobileRef, customersByNameRef, customerIndexStatsRef
  } = firebase;

  const trimmedSearch = (search || '').trim();
  const isDefaultFeed = !filter && !trimmedSearch;

  // Cold default-feed load: hydrate the whole accumulated feed from cache
  // (zero network) unless an explicit refresh/reset was requested.
  if (isDefaultFeed && !cursor && !reset) {
    const cached = await readCustomersPaged();
    if (cached && Array.isArray(cached.rows)) {
      dispatch({
        type: FETCH_CUSTOMERS_PAGE_SUCCESS,
        payload: { rows: cached.rows, nextCursor: cached.cursor, hasMore: cached.hasMore, reset: true }
      });
      try {
        const s = await get(customerIndexStatsRef);
        dispatch({ type: FETCH_CUSTOMERS_PAGE_SUCCESS, payload: { rows: [], nextCursor: cached.cursor, hasMore: cached.hasMore, reset: false, stats: s.val() || null } });
      } catch (_) { /* stats best-effort */ }
      return;
    }
  }

  dispatch({ type: FETCH_CUSTOMERS_PAGE, payload: null });
  try {
    let q;
    if (trimmedSearch) {
      if (/^[0-9+\s-]+$/.test(trimmedSearch)) {
        q = customersByMobileRef(normalizeMobileSearch(trimmedSearch));
      } else {
        q = customersByNameRef(trimmedSearch.toLowerCase(), pageSize);
      }
    } else if (filter === 'invited') {
      q = customersByInvitedRef(true, CUSTOMERS_FILTER_CAP);
    } else if (filter === 'without_invited') {
      q = customersByInvitedRef(false, CUSTOMERS_FILTER_CAP);
    } else if (filter === 'appless') {
      q = customersAppLessRef(CUSTOMERS_FILTER_CAP);
    } else {
      q = customersPageRef(cursor, pageSize);
    }

    const snap = await get(q);
    const val = snap.val() || {};
    // RTDB returns an unordered object in JS — sort ascending by createdAt.
    const ascending = Object.keys(val)
      .filter(k => k.indexOf('_') !== 0)
      .map(k => ({ ...val[k], id: k, uid: k }))
      .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

    let rows; let hasMore = false; let nextCursor = null;
    if (isDefaultFeed) {
      if (ascending.length > pageSize) {
        const extra = ascending[0];            // the (pageSize+1)th, oldest
        rows = ascending.slice(1).reverse();   // newest-first, pageSize rows
        const candidate = extra.createdAt;
        // Advance only if the cursor strictly moves. A tie cluster larger than
        // a page at the same createdAt (e.g. a bulk backfill) would otherwise
        // return candidate === cursor forever — stop "load more" cleanly
        // instead of looping. Also guard against a non-finite createdAt.
        if (Number.isFinite(candidate) && !(cursor != null && candidate === cursor)) {
          hasMore = true;
          nextCursor = candidate;
        }
      } else {
        rows = ascending.reverse();
      }
    } else {
      rows = ascending.reverse();              // filtered/search: bounded set, newest-first
    }

    let stats;
    try { const s = await get(customerIndexStatsRef); stats = s.val() || null; } catch (_) { stats = undefined; }

    const isAppend = isDefaultFeed && !!cursor && !reset;
    dispatch({
      type: FETCH_CUSTOMERS_PAGE_SUCCESS,
      payload: { rows, nextCursor, hasMore, reset: !isAppend, stats }
    });

    // Cache only the default newest-first feed (accumulating).
    if (isDefaultFeed) {
      try {
        const existing = isAppend ? await readCustomersPaged() : null;
        const byId = existing && existing.byId ? { ...existing.byId } : {};
        const accRows = existing && Array.isArray(existing.rows) ? existing.rows.slice() : [];
        rows.forEach(r => {
          if (!byId[r.id]) { byId[r.id] = r; accRows.push(r); }
          else { byId[r.id] = { ...byId[r.id], ...r }; }
        });
        await writeCustomersPaged({ rows: accRows, byId, cursor: nextCursor, hasMore });
      } catch (_) { /* cache best-effort */ }
    }
  } catch (error) {
    dispatch({ type: FETCH_CUSTOMERS_PAGE_FAILED, payload: (error && error.message) || 'Error fetching customers.' });
  }
};

// Lightweight read of the /customer_index_stats counter node — feeds the
// Dashboard customer tiles for call-center (whose users slice has no
// customers to count). Updates only the stats on the customers slice, leaving
// the loaded pages / cursor untouched.
export const fetchCustomerStats = () => async (dispatch) => {
  const { customerIndexStatsRef } = firebase;
  try {
    const snap = await get(customerIndexStatsRef);
    const cur = store.getState().usersdata.customers || {};
    dispatch({
      type: FETCH_CUSTOMERS_PAGE_SUCCESS,
      payload: { rows: [], reset: false, stats: snap.val() || null, nextCursor: cur.cursor, hasMore: cur.hasMore }
    });
  } catch (_) { /* stats best-effort */ }
};

// Portal dashboard "Fleet & app health": the nightly server snapshot at
// /dashboard_stats ({ drivers, customers }). One small read; the callers keep
// it in component state for the session (the data changes once a day).
export const fetchDashboardStats = async () => {
  const { dashboardStatsRef } = firebase;
  try {
    const snap = await get(dashboardStatsRef);
    return snap.val() || null;
  } catch (e) {
    // Surface "rules not deployed / role not allowed" distinctly so the
    // dashboard can say so instead of pretending the snapshot is pending.
    const err = new Error(e && e.message ? e.message : 'read failed');
    err.code = /permission/i.test(String(e && (e.code || e.message))) ? 'permission_denied' : 'read_failed';
    throw err;
  }
};

// Admin-only "Build now": asks the server to run the nightly rebuilds once
// (throttled there to one manual build per 6 hours). Resolves with the fresh
// { drivers, customers } snapshot.
export const buildDashboardStats = () => postWithIdToken('/build_dashboard_stats', {});

export const clearFetchDrivers = () => (dispatch) => {
  const {
    driversRef,
    allLocationsRef,
  } = firebase;
  off(driversRef);
  off(allLocationsRef);
}

// Live subscription to a single user record. Use this from admin views
// (DriverDetails, CustomerDetails) so wallet/topup/rating changes from
// any source — Cloud Function settlements after a mobile driver completes
// a trip, edits made by other admins, etc. — stream into Redux while the
// page is open. The dispatch updates both `users` and `staticusers` arrays
// via the UPSERT_USER reducer case, so any consumer (UserWalletDetails,
// UserRides, UserInfoDetails) re-renders with fresh data automatically.
//
// Returns the onValue unsubscribe function — call it from the component's
// useEffect cleanup to avoid leaking listeners across page navigations.
export const subscribeToUser = (id) => () => {
  if (!id) return () => {};
  const { singleUserRef } = firebase;
  const dbRef = singleUserRef(id);
  return onValue(dbRef, (snap) => {
    const userData = snap.val();
    if (userData) {
      store.dispatch({
        type: UPSERT_USER,
        payload: { id, patch: { ...userData, id } },
      });
    }
  });
};

// On-demand single-user refresh for the driver/customer detail pages' "Refresh"
// button. Fetches just THIS user's record (and, with withCars, just this
// driver's cars — indexed, tiny) so an agent sees a mobile upload of a car or
// document immediately, without re-downloading the whole /users or /cars tree.
// Returns a promise resolving to the fetched record ({ ...user, id }) or null
// if no such user — callers (e.g. CustomerDetails) can 404 off this result
// instead of off the (possibly not-yet-loaded) staticusers array.
export const refreshUser = (id, opts = {}) => async (dispatch) => {
  if (!id) return null;
  const { singleUserRef, carsByDriverRef } = firebase;
  const reads = [get(singleUserRef(id))];
  if (opts.withCars) reads.push(get(carsByDriverRef(id)));
  const [userSnap, carsSnap] = await Promise.all(reads);

  const u = userSnap && userSnap.val();
  if (u) {
    // Refreshes the record into Redux (`users` + `staticusers`) + the IDB cache.
    await patchUsersCacheUpsert(id, { ...u, id });
  }
  if (opts.withCars && carsSnap) {
    const cv = carsSnap.val() || {};
    const cars = Object.keys(cv).map((k) => ({ ...cv[k], id: k }));
    dispatch({ type: UPSERT_DRIVER_CARS, payload: { driver: id, cars } });
  }
  return u ? { ...u, id } : null;
};

export const fetchDrivers = (appType) => async (dispatch) => {
  // Reads from the slim projection node maintained by the
  // `rebuildActiveDriversSlim` Cloud Function instead of the full /users
  // tree. Payload drops from ~10 MB to ~30 KB at 3000 enrolled drivers
  // because:
  //   • The slim node only contains drivers that are approved, active,
  //     and have a recent location (filters applied server-side).
  //   • Each record carries only the 7 fields the customer UI needs,
  //     not the full user profile.
  // This is the difference between $1500/month and ~$5/month in RTDB
  // bandwidth at 20k DAU.
  const { activeDriversSlimRef } = firebase;

  dispatch({
    type: FETCH_ALL_USERS,
    payload: null,
  });

  try {
    const snap = await get(activeDriversSlimRef);
    const data = snap.val();
    if (data) {
      // Cloud Function already applied all eligibility filters and shaped
      // the records, so we just attach the id/uid and ship.
      // We expose BOTH `id` and `uid`:
      //   • `id`  — preserves backward compat with the original
      //     fetchDrivers, which only set `id`. Mobile code reads this.
      //   • `uid` — fixes a pre-existing silent bug in the web admin
      //     Dashboard that referenced `driver.uid` (always undefined
      //     before, now populated) for live-booking-on-marker matching.
      //
      // The _meta key is written by the Cloud Function as a placeholder
      // so the node never disappears (RTDB treats empty objects as null).
      // Filter it out so it's never treated as a driver.
      const arr = Object.keys(data)
        .filter((uid) => !uid.startsWith('_'))
        .map((uid) => ({
          id: uid,
          uid,
          ...data[uid],
        }));
      dispatch({
        type: FETCH_ALL_DRIVERS_SUCCESS,
        payload: arr,
      });
    } else {
      dispatch({
        type: FETCH_ALL_DRIVERS_FAILED,
        payload: 'No drivers available.',
      });
    }
  } catch (error) {
    dispatch({
      type: FETCH_ALL_DRIVERS_FAILED,
      payload: error.message || 'Error fetching drivers.',
    });
  }
};

// LIVE driver feed for the web admin Dashboard map. Unlike fetchDrivers (a
// one-time get of the slim snapshot), this subscribes so the map auto-updates,
// and it mirrors the dispatcher's "eligible driver at their live position":
//   • /eligible_drivers (kept REAL-TIME by the maintainEligibleDrivers Cloud
//     Function) is the SET of drivers to plot + their identity (carType, name,
//     vehicle, queue) — so a driver who just went online appears immediately,
//     and one who goes offline/busy drops immediately, with no 2-min lag.
//   • /locations supplies each driver's LIVE position. This is why an eligible
//     driver who isn't in the 2-min slim snapshot yet still shows on the map.
//   • /active_drivers_slim is a coords fallback (if a driver has no /locations
//     row yet) and the whole-map fallback if /eligible_drivers is unavailable
//     (e.g. its read rule isn't deployed) — so the map never goes blank.
// NOTE: the /locations listener is a LIVE QUERY bounded to rows updated in the
// last 24 h at attach time (recentLocationsRef, indexed on updateTime), not the
// full node — most of its ~7k rows are months stale and every Dashboard mount
// (the landing page for six staff roles) used to download all of them. A live
// query adds children as they enter the range, so a driver who starts reporting
// after attach still appears; a row already older than 24 h at attach is
// excluded (that driver falls back to the slim coords, or is not plotted).
// Web-only (sole caller: web-app Dashboard). Returns an unsubscribe function.
export const subscribeActiveDriversLive = () => (dispatch) => {
  const { recentLocationsRef, eligibleDriversRef, activeDriversSlimRef } = firebase;
  const liveLocationsQuery = recentLocationsRef(Date.now() - LOCATION_JOIN_WINDOW_MS);
  let locData = {};
  let eligData = {};
  let slimData = {};

  const recompute = () => {
    const hasElig = Object.keys(eligData).some((k) => !k.startsWith('_'));
    let arr;
    if (hasElig) {
      arr = Object.keys(eligData)
        .filter((uid) => !uid.startsWith('_'))
        .map((uid) => {
          const loc = locData[uid] || (slimData[uid] && slimData[uid].location);
          if (!loc || typeof loc.lat !== 'number' || typeof loc.lng !== 'number') return null;
          return {
            id: uid,
            uid,
            ...eligData[uid],
            location: { lat: loc.lat, lng: loc.lng, updateTime: loc.updateTime || null },
          };
        })
        .filter(Boolean);
    } else {
      // /eligible_drivers unavailable — fall back to the slim snapshot.
      arr = Object.keys(slimData)
        .filter((uid) => !uid.startsWith('_'))
        .map((uid) => ({ id: uid, uid, ...slimData[uid] }));
    }
    dispatch({ type: FETCH_ALL_DRIVERS_SUCCESS, payload: arr });
  };

  dispatch({ type: FETCH_ALL_USERS, payload: null });
  const offElig = onValue(eligibleDriversRef, (s) => { eligData = s.val() || {}; recompute(); }, () => { eligData = {}; recompute(); });
  const offSlim = onValue(activeDriversSlimRef, (s) => { slimData = s.val() || {}; recompute(); }, () => {});
  const offLoc = onValue(liveLocationsQuery, (s) => { locData = s.val() || {}; recompute(); }, () => {});

  return () => { offElig(); offSlim(); offLoc(); };
};

const generateUniqueCode = async (userType) => {
  const {
    usersRef
  } = firebase;

  const prefix = userType === "driver" ? "D" : "C";
  const uniquePart = Math.floor(100000 + Math.random() * 900000); // Generate a random 6-digit number
  const code = `${prefix}-${uniquePart}`;

  const fieldName = userType === "driver" ? "driverCode" : "customerCode";
  const usersQuery = query(usersRef, orderByChild(fieldName), equalTo(code));

  const snapshot = await get(usersQuery);
  if (snapshot.exists()) {
    return generateUniqueCode(userType);
  }

  return code;
};

export const addUser = (userdata) => async (dispatch) => {
  const {
    config,
    usersRef
  } = firebase;

  dispatch({
    type: EDIT_USER,
    payload: userdata
  });

  delete userdata.tableData;

  try {
    if (userdata.usertype === "driver" || userdata.usertype === "customer") {
      userdata[
          userdata.usertype === "driver" ? "driverCode" : "customerCode"
          ] = await generateUniqueCode(userdata.usertype);
    }

    // Capture the new key so we can surgically patch the cache instead of
    // dropping it — admins stay on the warm cache after adding a user.
    const newRef = await push(usersRef, userdata);
    if (newRef && newRef.key) {
      patchUsersCacheUpsert(newRef.key, userdata);
    } else {
      invalidateUsersCache();
    }
    dispatch({
      type: EDIT_USER_SUCCESS,
      payload: null,
    });

    // Send Welcome SMS
    const settings = store.getState().settingsdata.settings;
    const host = (typeof window !== 'undefined' && window.location && settings.CompanyWebsite === window.location.origin)
        ? window.location.origin
        : `https://${config.projectId}.web.app`;

    const url = `${host}/send_signup_welcome_sms`;
    try {
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + base64.encode(config.projectId + ':' + AccessKey)
        },
        body: JSON.stringify({
          rawPhone: userdata.mobile,
        })
      });
    } catch (err) {
      console.log('[SendSignupWelcomeSms] Error sending SMS:', err.message);
    }
  } catch (error) {
    console.error("Error adding user:", error);
    dispatch({
      type: EDIT_USER_FAILED,
      payload: error,
    });
  }
}

// Server-owned money, bonus and credit fields. The portal never edits them
// (Add money, withdraws, settlement and the server write them), and the
// cached row an edit form starts from (IndexedDB, up to 4 h old) would roll
// back newer server writes. The money-lock rules (R2) also refuse a change
// from most staff roles, and one refused field fails the WHOLE save.
const SERVER_OWNED_USER_FIELDS = [
  'walletBalance', 'topUpBalance', 'donkeyMiles',
  'weeklyTripCount', 'weeklyTripRevenue', 'weeklyTiersAwarded', 'weekStartTs',
  'monthlyBonusEarned', 'monthStartTs',
  'welcomeCreditAmount', 'welcomeCreditGrantedAt', 'holidayPromoGrantedAt', 'holidayPromoAmount',
  'onboardingCompletedAt', 'onboardingRole', 'onboardingVersion', 'onboardingCanary',
  'activeBookingId',
];

// Approval flags the caller may not set on an existing record (the same
// limits as the R2 rules on users/$uid): other staff may only lower
// adminApprovedTrue and offline_meter_enabled (false), may raise carApproved
// only while car approval is off, and nobody but an admin raises their OWN
// approved / adminApprovedTrue. Such a value is left out, so the rest of the
// save still lands. null (a delete) is not limited by those rules.
const approvalFlagRefused = (field, value, ctx) => {
  if (ctx.isAdmin || value === null) return false;
  switch (field) {
    case 'approved': return ctx.isSelf && value !== false;
    case 'adminApprovedTrue': return ctx.isSelf || value !== false;
    case 'offline_meter_enabled': return value !== false;
    case 'carApproved': return value !== false && !(value === true && ctx.settings.carApproval !== true);
    default: return false;
  }
};

const editCallerContext = (id) => {
  const state = store.getState() || {};
  const me = (state.auth && state.auth.profile) || {};
  const { auth } = firebase;
  const myUid = (auth && auth.currentUser && auth.currentUser.uid) || me.uid || null;
  return {
    isAdmin: me.usertype === 'admin',
    isSelf: !!myUid && myUid === id,
    settings: (state.settingsdata && state.settingsdata.settings) || {}
  };
};

// Portal Edit User / Internal Staff save.
//   editUser(id, user, oldUser?)
// id = null: the Internal Staff "add" path (mints a push key; unchanged).
// id set: an existing record. Written: only the fields that differ from
// oldUser (the row the form started from; without oldUser every field of
// `user` is sent, as before), never a server-owned field, and never an
// approval flag the caller may not set.
// Returns a promise of { success, id, fields } | { success: true, unchanged:
// true } | { success: false, id, code, error } (a refused write; the cached
// row is put back when oldUser was given). Callers that ignore it behave as
// before.
export const editUser = (id, user, oldUser) => (dispatch) => {

  const {
    singleUserRef,
    usersRef
  } = firebase;

  dispatch({
    type: EDIT_USER,
    payload: user
  });
  const editedUser = { ...user };
  delete editedUser.id;
  delete editedUser.tableData;
  // The retired auto-saved trip list (purged by the weekly userDataCleanup):
  // a cached row may still carry it, and update() would write it all back.
  delete editedUser.savedAddresses;
  if (id) {
    SERVER_OWNED_USER_FIELDS.forEach((k) => { delete editedUser[k]; });
    if (oldUser) {
      Object.keys(editedUser).forEach((k) => {
        if (JSON.stringify(editedUser[k]) === JSON.stringify(oldUser[k])) delete editedUser[k];
      });
    }
    const ctx = editCallerContext(id);
    Object.keys(editedUser).forEach((k) => {
      if (approvalFlagRefused(k, editedUser[k], ctx)) delete editedUser[k];
    });
  }
  // undefined means "no value", never a delete (update() throws on it).
  Object.keys(editedUser).forEach((k) => { if (editedUser[k] === undefined) delete editedUser[k]; });
  if (id && Object.keys(editedUser).length === 0) {
    return Promise.resolve({ success: true, id, unchanged: true, fields: [] });
  }
  // No id = the Internal Staff "add" path: mint a real push key (it used to
  // write every new staff record to /users/null). Staff accounts take this
  // path rather than addUser() so no signup welcome SMS goes out.
  const key = id || push(usersRef).key;
  // update(), not set(): the Edit User form carries a cached row, and a whole-node
  // set() wiped every key it does not know (driver opt-outs such as cityEventsPushOff,
  // server stamps). No form path ever removes a key, so partial writes are equivalent.
  const written = update(singleUserRef(key), editedUser);
  // We already have the post-edit data — patch the cache directly so the
  // next page navigation doesn't trigger a fresh /users fetch.
  patchUsersCacheUpsert(key, editedUser);
  return written.then(
    () => ({ success: true, id: key, fields: Object.keys(editedUser) }),
    (error) => {
      console.warn('[editUser] save refused for ' + key + ':', (error && error.message) || error);
      dispatch({
        type: EDIT_USER_FAILED,
        payload: error
      });
      // Put the cached row back (local only, nothing is downloaded).
      if (id && oldUser) {
        const revert = {};
        Object.keys(editedUser).forEach((k) => { revert[k] = oldUser[k] === undefined ? null : oldUser[k]; });
        patchUsersCacheUpsert(key, revert);
      }
      return { success: false, id: key, code: (error && error.code) || null, error: (error && error.message) || String(error) };
    }
  );
}

export const updateUserCar = (id, data) => (dispatch) => {
  const {
    singleUserRef
  } = firebase;

  dispatch({
    type: EDIT_USER,
    payload: data
  });
  update(singleUserRef(id),data);
  // Return the cache-coherence promise so web callers can await it before
  // navigating away. Without this, the next page's cache-first user fetch
  // (fetchUsersOnce in ResponsiveDrawer / Drivers) can read the not-yet-patched
  // cache and revert the change — making the driver's compliance look stale.
  // No-op on mobile (RN has no IDB cache; the awaited promise resolves at once).
  return patchUsersCacheUpsert(id, data);
}

// Partial profile update: writes ONLY the given fields (RTDB update, never
// set) and patches the cache. Use this for every switch / single-field edit
// in the portal. editUser() replaces the whole node from the caller's (often
// cached, often stale) row and wipes anything written server-side since —
// carApproved, vehiclePlaceholder, term, commissionMode, credit and SMS
// stamps, a document uploaded minutes earlier. Same body as updateUserCar,
// named for what it is.
export const updateUserFields = (id, fields) => updateUserCar(id, fields);

export const updateLicenseImage = (uid, imageBlob, imageType) => async (dispatch) => {

  const {
    singleUserRef,
    driverDocsRef,
    driverDocsRefBack,
    verifyIdImageRef,
    insuranceImageRef
  } = firebase;

  let profile = {};
  if(imageType === 'licenseImage'){
    await uploadBytesResumable(driverDocsRef(uid),imageBlob);
    let image = await getDownloadURL(driverDocsRef(uid));
    profile.licenseImage = image;
  }
  if(imageType === 'licenseImageBack'){
    await uploadBytesResumable(driverDocsRefBack(uid),imageBlob);
    let image1 = await getDownloadURL(driverDocsRefBack(uid));
    profile.licenseImageBack = image1;
  }
  if(imageType === 'verifyIdImage'){
    await uploadBytesResumable(verifyIdImageRef(uid),imageBlob);
    let image1 = await getDownloadURL(verifyIdImageRef(uid));
    profile.verifyIdImage = image1;
  }
  if(imageType === 'insuranceImage') {
    await uploadBytesResumable(insuranceImageRef(uid),imageBlob);
    let image1 = await getDownloadURL(insuranceImageRef(uid));
    profile.insuranceImage = image1;
  }
  update(singleUserRef(uid),profile);
  // Await cache coherence before resolving so callers that await this thunk
  // (UserDocuments / UnifiedDriverOnboarding) don't trigger a refetch that
  // reads a not-yet-patched cache and reverts the new document URL. The
  // UPSERT_USER dispatch inside also keeps mounted views fresh immediately.
  await patchUsersCacheUpsert(uid, profile);
  dispatch({
    type: EDIT_USER,
    payload: uid
  });
};

export const deleteUser = (uid) => async (dispatch) => {

  const {
    auth,
    walletHistoryRef,
    singleUserRef,
    userNotificationsRef,
    carsRef,
    carEditRef
  } = firebase;

  dispatch({
    type: DELETE_USER,
    payload: uid
  });

  if (auth.currentUser.uid === uid) {
    off(singleUserRef(uid));
    off(walletHistoryRef(uid));
    off(userNotificationsRef(uid));
  }

  try {
    const userSnap = await get(singleUserRef(uid));
    const profile = userSnap.val();
    if (profile && profile.usertype === 'driver') {
      const carsSnap = await get(carsRef(uid, profile.usertype));
      let cars = carsSnap.val();
      if (cars) {
        const arr = Object.keys(cars);
        for (let i = 0; i < arr.length; i++) {
          remove(carEditRef(arr[i]));
        }
      }
    }

    remove(singleUserRef(uid)).then(() => {
      patchUsersCacheDelete(uid);
      if (auth.currentUser.uid === uid) {
        signOut(auth);
        dispatch({
          type: USER_DELETED,
          payload: null
        });
      } else {
        remove(singleUserRef(uid)).then(() => {
          dispatch({
            type: DELETE_USER_SUCCESS,
            payload: null
          });
        }).catch((error) => {
          dispatch({
            type: DELETE_USER_FAILED,
            payload: error
          });
        });
      }
    });
  } catch (error) {
    dispatch({
      type: DELETE_USER_FAILED,
      payload: error
    });
  }
}

export const fetchOtpAuthRequests = (mobile) => async (dispatch) => {
  const { otpAuthByMobileRef } = firebase;

  dispatch({
    type: FETCH_OTP_AUTH_REQUESTS,
    payload: null
  });

  try {
    const snapshot = await get(otpAuthByMobileRef(mobile));
    
    if (snapshot.exists()) {
      const data = snapshot.val();
      // Convert object to array and get the most recent entry
      const otpEntries = Object.keys(data).map(key => ({
        id: key,
        ...data[key]
      }));
      
      // Sort by 'dated' field to get the most recent
      const latestOtp = otpEntries.sort((a, b) => b.dated - a.dated)[0];
      
      dispatch({
        type: FETCH_OTP_AUTH_REQUESTS_SUCCESS,
        payload: latestOtp
      });
    } else {
      console.debug('No OTP data found for mobile:', mobile);
      dispatch({
        type: FETCH_OTP_AUTH_REQUESTS_SUCCESS,
        payload: null
      });
    }
  } catch (error) {
    console.error('Error fetching OTP auth requests:', error);
    dispatch({
      type: FETCH_OTP_AUTH_REQUESTS_FAILED,
      payload: error.message
    });
  }
}
